export interface INavigationRoute {
  name?: string
  displayName: string
  // requiresPlan mirrors the router's route.meta.requiresPlan — set on nav items that
  // point at a Community-plan-gated route so the sidebar can show a lock badge for
  // locked (free) tenants without hiding the item (they should see what unlocks).
  meta: { icon: string; roles?: string[]; requiresPlan?: string }
  children?: INavigationRoute[]
}

export default {
  root: {
    name: '/',
    displayName: 'navigationRoutes.home',
  },
  routes: [
    // ===================== Admin / staff =====================
    {
      name: 'dashboard',
      displayName: 'menu.dashboard',
      meta: {
        icon: 'vuestic-iconset-dashboard',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'],
      },
    },
    {
      displayName: 'menu.sectionCommunity',
      meta: {
        icon: 'mso-groups',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'],
      },
      children: [
        {
          name: 'announcements',
          displayName: 'menu.announcements',
          meta: { icon: 'mso-campaign', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'] },
        },
        {
          name: 'incidents',
          displayName: 'menu.incidents',
          meta: { icon: 'mso-report', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'] },
        },
        {
          name: 'amenities',
          displayName: 'menu.amenities',
          meta: { icon: 'mso-event_available', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
      ],
    },
    {
      displayName: 'menu.sectionDirectory',
      meta: {
        icon: 'mso-domain',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'],
      },
      children: [
        {
          name: 'households',
          displayName: 'menu.households',
          meta: { icon: 'home', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
        {
          name: 'streets',
          displayName: 'menu.streets',
          meta: { icon: 'signpost', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
        {
          name: 'buildings',
          displayName: 'menu.buildings',
          meta: { icon: 'apartment', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
        {
          name: 'household-types',
          displayName: 'menu.householdTypes',
          meta: { icon: 'mso-category', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
      ],
    },
    {
      displayName: 'menu.sectionBilling',
      meta: {
        icon: 'mso-account_balance_wallet',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'],
      },
      children: [
        {
          name: 'billing',
          displayName: 'menu.billing',
          meta: { icon: 'mso-receipt_long', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'payment-methods',
          displayName: 'menu.payments',
          meta: { icon: 'mso-payments', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'stickers',
          displayName: 'menu.stickers',
          meta: { icon: 'mso-local_offer', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'member-ledger',
          displayName: 'menu.memberLedger',
          meta: { icon: 'mso-account_balance', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'credit-ledger',
          displayName: 'menu.creditLedger',
          meta: { icon: 'mso-savings', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'reports',
          displayName: 'menu.reports',
          meta: { icon: 'mso-analytics', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
      ],
    },
    {
      displayName: 'menu.sectionGateVisitors',
      meta: {
        icon: 'mso-shield',
        roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'],
      },
      children: [
        {
          name: 'guard-home',
          displayName: 'menu.guardHome',
          meta: { icon: 'mso-home', roles: ['GUARD'] },
        },
        {
          name: 'guard-gate',
          displayName: 'menu.guardGate',
          meta: { icon: 'mso-pin_drop', roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-visitors',
          displayName: 'menu.visitorCenter',
          meta: { icon: 'mso-badge', roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'visitor-passes',
          displayName: 'menu.visitorPasses',
          meta: { icon: 'mso-qr_code_2', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-visitor-requests',
          displayName: 'menu.guardVisitorRequests',
          meta: { icon: 'mso-verified', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-visitor-directory',
          displayName: 'menu.visitorDirectory',
          meta: { icon: 'mso-contacts', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-deliveries',
          displayName: 'menu.guardDeliveries',
          meta: { icon: 'mso-local_shipping', roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-visitor-pass-scan',
          displayName: 'menu.visitorPassScan',
          meta: { icon: 'mso-qr_code_scanner', roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'guard-announcements',
          displayName: 'menu.myAnnouncements',
          meta: { icon: 'mso-campaign', roles: ['GUARD'] },
        },
        {
          name: 'guard-incidents',
          displayName: 'menu.myIncidents',
          meta: { icon: 'mso-report', roles: ['GUARD'] },
        },
      ],
    },
    {
      displayName: 'menu.sectionOversight',
      meta: {
        icon: 'mso-fact_check',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'],
      },
      children: [
        {
          name: 'audit-annual',
          displayName: 'menu.auditAnnual',
          meta: { icon: 'mso-assignment', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'audit-logs',
          displayName: 'menu.auditLogs',
          meta: { icon: 'mso-history', roles: ['SUPER_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
        },
      ],
    },
    {
      displayName: 'menu.sectionAdministration',
      meta: {
        icon: 'mso-admin_panel_settings',
        roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'],
      },
      children: [
        {
          name: 'users',
          displayName: 'menu.users',
          meta: { icon: 'group', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
        {
          name: 'invite-homeowners',
          displayName: 'menu.inviteHomeowners',
          meta: { icon: 'mso-qr_code_2', roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
        },
        {
          name: 'subscription',
          displayName: 'menu.subscription',
          meta: { icon: 'mso-credit_card', roles: ['ASSOCIATION_ADMIN'] },
        },
      ],
    },
    {
      displayName: 'menu.sectionPlatform',
      meta: {
        icon: 'mso-cloud',
        roles: ['SUPER_ADMIN'],
      },
      children: [
        {
          name: 'tenants',
          displayName: 'menu.tenants',
          meta: { icon: 'mso-apartment', roles: ['SUPER_ADMIN'] },
        },
        {
          name: 'plans',
          displayName: 'menu.plans',
          meta: { icon: 'mso-sell', roles: ['SUPER_ADMIN'] },
        },
        {
          name: 'subscriptions',
          displayName: 'menu.subscriptions',
          meta: { icon: 'mso-payments', roles: ['SUPER_ADMIN'] },
        },
        {
          name: 'contact-inquiries',
          displayName: 'menu.contactInquiries',
          meta: { icon: 'mso-mail', roles: ['SUPER_ADMIN'] },
        },
        {
          name: 'email-templates',
          displayName: 'menu.emailTemplates',
          meta: { icon: 'mso-mail', roles: ['SUPER_ADMIN'] },
        },
      ],
    },

    // ===================== Homeowner =====================
    {
      name: 'homeowner-dashboard',
      displayName: 'menu.myDashboard',
      meta: {
        icon: 'vuestic-iconset-dashboard',
        roles: ['HOMEOWNER'],
      },
    },
    {
      displayName: 'menu.sectionHomeownerFinance',
      meta: {
        icon: 'mso-account_balance_wallet',
        roles: ['HOMEOWNER'],
      },
      children: [
        {
          name: 'homeowner-bills',
          displayName: 'menu.myBills',
          meta: { icon: 'mso-receipt_long', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-payments',
          displayName: 'menu.myPayments',
          meta: { icon: 'mso-payments', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-credits',
          displayName: 'menu.myCredits',
          meta: { icon: 'mso-account_balance_wallet', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-stickers',
          displayName: 'menu.myStickers',
          meta: { icon: 'mso-local_offer', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
      ],
    },
    {
      displayName: 'menu.sectionHomeownerAccess',
      meta: {
        icon: 'mso-security',
        roles: ['HOMEOWNER'],
      },
      children: [
        {
          name: 'homeowner-vehicles',
          displayName: 'menu.myVehicles',
          meta: { icon: 'mso-directions_car', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-visitors',
          displayName: 'menu.visitorCenter',
          meta: { icon: 'mso-badge', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-deliveries',
          displayName: 'menu.myDeliveries',
          meta: { icon: 'mso-local_shipping', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
        {
          name: 'homeowner-visitor-passes',
          displayName: 'menu.myVisitorPasses',
          meta: { icon: 'mso-qr_code_2', roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
        },
      ],
    },
    {
      displayName: 'menu.sectionHomeownerAmenities',
      meta: {
        icon: 'mso-event',
        roles: ['HOMEOWNER'],
      },
      children: [
        {
          name: 'homeowner-bookings',
          displayName: 'menu.myBookings',
          meta: { icon: 'mso-event', roles: ['HOMEOWNER'] },
        },
        {
          name: 'homeowner-announcements',
          displayName: 'menu.myAnnouncements',
          meta: { icon: 'mso-campaign', roles: ['HOMEOWNER'] },
        },
      ],
    },
    {
      displayName: 'menu.sectionHomeownerCommunity',
      meta: {
        icon: 'mso-report',
        roles: ['HOMEOWNER'],
      },
      children: [
        {
          name: 'homeowner-incidents',
          displayName: 'menu.myIncidents',
          meta: { icon: 'mso-report', roles: ['HOMEOWNER'] },
        },
      ],
    },
  ] as INavigationRoute[],
}
